import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'

import BodyText from 'LuxKit/Typography/BodyText'
import InputText from 'LuxKit/Typography/InputText'
import Input from 'components/Common/Form/Input/Input'
import { MAX_TRIP_NAME_CHARS } from 'tripPlanner/config'

export type RecentItemTitleVariant = 'small' | 'medium'

const TitleInput = styled(Input)<{ $variant: RecentItemTitleVariant }>`
  width: 100%;
  min-width: 0;
  border: 1px solid ${props => props.theme.palette.brand.primary.normal};
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(2)} ${rem(6)};
  color: ${props => props.theme.palette.neutral.default.one};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  font-size: ${props => props.$variant === 'medium' ? rem(16) : rem(14)};
  font-weight: 700;
  line-height: ${props => props.$variant === 'medium' ? rem(24) : rem(20)};
  outline: none;

  &:focus {
    box-shadow: 0 0 0 2px ${props => props.theme.palette.brand.primary.light};
  }
`

const ErrorText = styled(InputText)`
  margin-top: ${rem(2)};
`

interface Props {
  title?: string
  fallbackTitle: string
  isEditing: boolean
  variant: RecentItemTitleVariant
  onEditingChange: (isEditing: boolean) => void
  onRename: (name: string) => Promise<void>
}

export default function RecentItemInlineTitleEdit({
  title,
  fallbackTitle,
  isEditing,
  variant,
  onEditingChange,
  onRename,
}: Props) {
  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [displayTitle, setDisplayTitle] = useState(title?.trim() || fallbackTitle)
  const [draftTitle, setDraftTitle] = useState(displayTitle)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [isSaving, setIsSaving] = useState(false)
  const isSavingRef = useRef(false)

  useEffect(() => {
    const nextTitle = title?.trim() || fallbackTitle
    setDisplayTitle(nextTitle)
    setDraftTitle(nextTitle)
    setErrorMessage(undefined)
  }, [fallbackTitle, title])

  useEffect(() => {
    if (isEditing) {
      setDraftTitle(displayTitle)
      window.requestAnimationFrame(() => {
        inputRef.current?.focus()
        inputRef.current?.select()
      })
    }
  }, [displayTitle, isEditing])

  const emptyNameError = intl.formatMessage({
    id: 'O3TfpOALSbrat4zK4YFl',
    defaultMessage: 'Name cannot be empty',
    description: 'Inline validation message when a recent trip planner chat or trip rename is empty',
  })

  const renameFailedError = intl.formatMessage({
    id: 'svK_yN91_Xg6AenKk3hb',
    defaultMessage: 'Unable to rename. Please try again.',
    description: 'Inline validation message when renaming a recent trip planner chat or trip fails',
  })

  const renameInputLabel = intl.formatMessage({
    id: 'j3PZWc-b5voroPmGdGIW',
    defaultMessage: 'Rename',
    description: 'Accessible label for the inline rename input on recent trip planner items',
  })

  const commitRename = useCallback(async() => {
    if (isSavingRef.current) {
      return
    }

    const trimmedTitle = draftTitle.trim()
    if (!trimmedTitle) {
      setErrorMessage(emptyNameError)
      window.requestAnimationFrame(() => inputRef.current?.focus())
      return
    }

    if (trimmedTitle === displayTitle) {
      onEditingChange(false)
      return
    }

    isSavingRef.current = true
    setIsSaving(true)
    try {
      await onRename(trimmedTitle)
      setDisplayTitle(trimmedTitle)
      onEditingChange(false)
    } catch {
      setErrorMessage(renameFailedError)
      window.requestAnimationFrame(() => inputRef.current?.focus())
    } finally {
      isSavingRef.current = false
      setIsSaving(false)
    }
  }, [displayTitle, draftTitle, emptyNameError, onEditingChange, onRename, renameFailedError])

  const cancelRename = useCallback(() => {
    setDraftTitle(displayTitle)
    setErrorMessage(undefined)
    onEditingChange(false)
  }, [displayTitle, onEditingChange])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setDraftTitle(event.currentTarget.value)
    if (errorMessage) {
      setErrorMessage(undefined)
    }
  }, [errorMessage])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitRename().catch(() => undefined)
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      cancelRename()
    }
  }, [cancelRename, commitRename])

  const stopClickPropagation = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation()
  }, [])

  if (!isEditing) {
    return (
      <BodyText variant={variant} weight="bold" align="start" lineClamp={1}>
        {displayTitle}
      </BodyText>
    )
  }

  return (
    <>
      <TitleInput
        ref={inputRef}
        aria-label={renameInputLabel}
        value={draftTitle}
        maxLength={MAX_TRIP_NAME_CHARS}
        disabled={isSaving}
        $variant={variant}
        onChange={handleChange}
        onBlur={commitRename}
        onKeyDown={handleKeyDown}
        onClick={stopClickPropagation}
        onMouseDown={stopClickPropagation}
      />
      {!!errorMessage && (
        <ErrorText variant="caption" colour="critical" role="alert">
          {errorMessage}
        </ErrorText>
      )}
    </>
  )
}
